'use client'

import React, { useMemo, useState } from 'react'
import { Message } from '@locmod/intl'
import cx from 'classnames'

import { Icon } from 'components/ui'
import { Warning } from 'components/feedback'
import ConnectButtonWrapper from 'compositions/ConnectButtonWrapper/ConnectButtonWrapper'
import { useTicket } from '@/contexts/TicketContext'

import {
  AmountInput,
  SelectFreeBet as Freebet,
  Slippage,
  QuickBet,
} from './components'

import OvertimeBetButton from '@/Overtime/Components/OvertimeBetButton/OvertimeBetButton'

import messages from './messages'
import { MarketStatus } from '@/Overtime/enums/markets'
import { useBalance, useChainId } from 'wagmi'
import OvertimeCard from './components/Card/OvertimeCard'
import { useWallet } from 'wallet'
import OvertimeChips from './components/Chips/OvertimeChips'
import {
  OvertimeBetslipDisableReason,
  useDetailedOvertimeBetslip,
} from '@/contexts/DetailedOvertimeBetslipContext'
import { useIsMounted } from '@/hooks'
import multipleCollateralContract from '@/Overtime/utils/contracts/multipleCollateralContract'

const EmptyContent: React.FC = () => {
  return (
    <div className="max-w-64 text-center mx-auto my-6">
      <img
        className="size-16 mx-auto"
        src="/images/illustrations/betslip.png"
        alt=""
      />
      <Message
        className="text-heading-h5 font-bold mt-4"
        value={messages.empty.title}
        tag="p"
      />
      <Message
        className="text-caption-13 mt-2 text-accent-grey-200"
        value={messages.empty.text}
        tag="p"
      />
    </div>
  )
}

type SettingsProps = {
  onClose: () => void
}

const Settings: React.FC<SettingsProps> = ({ onClose }) => {
  return (
    <div>
      <div className="py-3 px-4 flex items-center justify-between">
        <Message
          className="text-caption-14 font-semibold"
          value={messages.settings}
        />
        <button
          className="text-accent-grey-200 hover:text-accent-grey-30 transition"
          onClick={onClose}
        >
          <Icon className="size-5" name="interface/close" />
        </button>
      </div>
      <div className="space-y-2">
        <Slippage />
        <QuickBet />
      </div>
    </div>
  )
}

type TabProps = {
  title: Intl.Message
  isActive: boolean
  onClick: () => void
}

const Tab: React.FC<TabProps> = ({ title, isActive, onClick }) => {
  return (
    <button
      className={cx('pb-3 relative', { 'text-brand-light-500': isActive })}
      onClick={onClick}
    >
      <Message className="text-caption-14 font-semibold" value={title} />
      {isActive && (
        <div className="absolute w-full bottom-0 left-0 h-0.5 bg-brand-light-500 rounded-t-full" />
      )}
    </button>
  )
}

type ContentProps = {
  openSettings: () => void
  openMarkets: boolean
}

const Content: React.FC<ContentProps> = ({ openSettings, openMarkets }) => {
  // OVERTIME
  const { account: address } = useWallet()
  const { ticket, removeAll } = useTicket()
  const chainId = useChainId()
  const { changeBetAmount } = useDetailedOvertimeBetslip()
  const { betAmount, minBet, maxBet } = useDetailedOvertimeBetslip()
  const isMounted = useIsMounted()

  // Add odds and states
  const { odds, states } = useDetailedOvertimeBetslip()

  // Check if all items are active
  const areAllItemsActive = ticket.every(
    item => states[item.gameId] === 'active'
  )

  const handleRemoveAll = (event: React.MouseEvent) => {
    event.preventDefault()
    removeAll(() => changeBetAmount(''))
  }

  // DISABLED REASONs
  const isAmountLowerThanMaxBet =
    Boolean(betAmount) && typeof maxBet !== 'undefined'
      ? +betAmount <= +maxBet
      : true

  const isAmountBiggerThanMinBet =
    Boolean(betAmount) && typeof minBet !== 'undefined'
      ? +betAmount >= minBet
      : !Boolean(betAmount)

  let disableReason = (() => {
    if (!areAllItemsActive) {
      return OvertimeBetslipDisableReason.ConditionState
    }
    if (!isAmountLowerThanMaxBet) {
      return OvertimeBetslipDisableReason.BetAmountGreaterThanMaxBet
    }
    if (!isAmountBiggerThanMinBet) {
      return OvertimeBetslipDisableReason.BetAmountLowerThanMinBet
    }
  })()

  const { data, isFetching: isBalanceFetching } = useBalance({
    address,
    token: multipleCollateralContract.USDC.addresses[chainId] as `0x${string}`,
  })

  const { formatted: balance, symbol } = data || {}

  const itemsLength = ticket.length
  const isSingle = itemsLength === 1

  const isEnoughBalance =
    isBalanceFetching || !Boolean(+betAmount)
      ? true
      : Boolean(+balance! >= +betAmount)

  if (!isMounted) {
    return null // Return null on server-side and first render
  }

  return (
    <div>
      <div
        className={cx('px-4 flex justify-between', {
          'py-3': isSingle,
          'pt-3': !isSingle,
        })}
      >
        {ticket.length === 1 ? (
          <button className="cursor-default">
            <Message
              className="text-caption-14 font-semibold"
              value={messages.single}
            />
          </button>
        ) : (
          <button className="cursor-default">
            <Message
              className="text-caption-14 font-semibold"
              value={{ ...messages.combo, values: { count: itemsLength } }}
            />
          </button>
        )}
        <div
          className={cx('flex items-center space-x-3 h-fit', {
            'pt-0.5': !isSingle,
          })}
        >
          <button
            className="text-accent-grey-200 hover:text-accent-grey-30 transition"
            onClick={openSettings}
          >
            <Icon className="size-5" name="interface/settings" />
          </button>
          <button
            className="text-accent-grey-200 hover:text-accent-grey-30 transition"
            onClick={handleRemoveAll}
          >
            <Icon className="size-5" name="interface/delete" />
          </button>
        </div>
      </div>
      <div
        className={cx('space-y-2 max-h-[24rem] overflow-auto no-scrollbar', {
          'pb-6': !isSingle,
          'pb-2': isSingle,
        })}
      >
        {ticket.map(item => {
          const itemOdds = odds[item.gameId]
          const itemState = states[item.gameId]

          return (
            <OvertimeCard
              key={item.gameId}
              item={item}
              isOddsFetching={false}
              isStatesFetching={false}
              odds={itemOdds}
              state={itemState}
            />
          )
        })}
      </div>
      <div
        className={cx({
          '-mt-4': !isSingle,
          'shadow-betslip rounded-lg': ticket.length > 2,
        })}
      >
        <Freebet />
        <div className="bg-accent-grey-850 p-3 rounded-lg z-10 relative">
          {/* Boolean(!selectedFreeBet && !isBatch) && ( */}
          <>
            <AmountInput isEnoughBalance={isEnoughBalance} />
            <OvertimeChips />
          </>
          {/* ) */}
          {Boolean(disableReason) && (
            <Warning
              className="mt-3"
              text={{
                ...messages.warnings[disableReason!],
                values: {
                  minBet,
                  maxBet,
                  symbol: symbol,
                },
              }}
            />
          )}
          <div className="mt-3">
            <ConnectButtonWrapper>
              <OvertimeBetButton
                isEnoughBalance={isEnoughBalance}
                isBalanceFetching={isBalanceFetching}
                openMarkets={openMarkets}
              />
            </ConnectButtonWrapper>
          </div>
        </div>
      </div>
    </div>
  )
}

const OvertimeBetslip: React.FC = () => {
  const { ticket } = useTicket()

  const [isSettingsVisible, setSettingsVisible] = useState(false)

  if (!ticket.length) {
    return <EmptyContent />
  }

  return (
    <>
      {isSettingsVisible ? (
        <Settings onClose={() => setSettingsVisible(false)} />
      ) : (
        <Content
          openSettings={() => setSettingsVisible(true)}
          openMarkets={false}
        />
      )}
    </>
  )
}

export default OvertimeBetslip
